import React from 'react';
import { REQUIRED } from '../../model/entryValidation';
import type { EntryErrors } from '../../model/entryValidation';

export interface FieldStyle {
  isMobile: boolean;
  disabled?: boolean;
}

/** Text input / select classes; red border when the field has an error */
export const inputCls = ({ isMobile, disabled }: FieldStyle, invalid = false): string =>
  `w-full px-2 ${isMobile ? 'text-base' : 'text-base md:text-sm'} border rounded bg-white focus:outline-none focus:ring-1 ${
    invalid ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
  } ${isMobile ? 'py-2.5 min-h-[44px]' : 'py-1'} ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`;

/** Label + control + inline error. A plain "Required" is shown only once the user touched the form (see `showRequired`). */
export const Field: React.FC<{
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  showRequired?: boolean;
  labelClassName?: string;
  className?: string;
  /** Phase 18U U8: an amber note that does not block saving (e.g. "Unusual value") */
  hint?: string;
  children: React.ReactNode;
}> = ({ id, label, error, required = false, showRequired = false, labelClassName = 'text-gray-700', className = '', hint, children }) => {
  const visibleError = error && (error !== REQUIRED || showRequired) ? error : '';
  return (
    <div className={`space-y-0.5 ${className}`}>
      <label htmlFor={id} className={`text-xs font-medium ${labelClassName}`}>
        {label}
        {required && <span className="text-red-600" aria-hidden="true"> *</span>}
      </label>
      {children}
      {visibleError && (
        <p id={`${id}-error`} className="text-xs text-red-600" data-testid={`error-${id}`}>
          {visibleError}
        </p>
      )}
      {!visibleError && hint && (
        <p className="text-xs text-amber-800" data-testid={`hint-${id}`}>{hint}</p>
      )}
    </div>
  );
};

/** Props for one entry type's fields */
export interface FieldsProps<T> extends FieldStyle {
  value: T;
  onChange: (next: T) => void;
  errors: EntryErrors;
  /** Show "Required" messages (after the first change) */
  showRequired: boolean;
}

/** aria props for an input with an error */
export const ariaFor = (id: string, error: string | undefined, showRequired: boolean) => {
  const shown = Boolean(error && (error !== REQUIRED || showRequired));
  return { 'aria-invalid': shown || undefined, 'aria-describedby': shown ? `${id}-error` : undefined };
};

/** A small segmented control (radio group) */
export const Segmented: React.FC<{
  name: string;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  showRequired?: boolean;
  isMobile: boolean;
}> = ({ name, label, options, value, onChange, disabled, required, error, showRequired = false, isMobile }) => {
  const visibleError = error && (error !== REQUIRED || showRequired) ? error : '';
  return (
    <div className="space-y-0.5">
      <div id={`${name}-label`} className="text-xs font-medium text-gray-700">
        {label}
        {required && <span className="text-red-600" aria-hidden="true"> *</span>}
      </div>
      <div role="radiogroup" aria-labelledby={`${name}-label`} className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChange(o.value)}
              className={`rounded border px-3 text-sm font-medium ${isMobile ? 'min-h-[44px]' : 'py-1'} ${
                active ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      {visibleError && <p className="text-xs text-red-600" data-testid={`error-${name}`}>{visibleError}</p>}
    </div>
  );
};

/** Multi-select as checkboxes */
export const CheckboxGroup: React.FC<{
  name: string;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}> = ({ name, label, options, value, onChange, disabled }) => (
  <fieldset className="space-y-0.5">
    <legend className="text-xs font-medium text-gray-700">{label}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <label key={o.value} className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm ${value.includes(o.value) ? 'border-slate-700 bg-slate-50 text-slate-900' : 'border-gray-200 text-gray-700'}`}>
          <input
            type="checkbox"
            name={name}
            disabled={disabled}
            className="h-5 w-5 rounded border-gray-300"
            checked={value.includes(o.value)}
            onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((v) => v !== o.value))}
          />
          {o.label}
        </label>
      ))}
    </div>
  </fieldset>
);

/** [{value, label}] for an enum of codes */
export const codeOptions = (codes: readonly string[], label: (code: string) => string) => codes.map((c) => ({ value: c, label: label(c) }));

interface BaseInputProps extends FieldStyle {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  showRequired?: boolean;
  labelClassName?: string;
  className?: string;
}

/** Labelled text or number input */
export const InputField: React.FC<BaseInputProps & {
  type?: 'text' | 'number' | 'date' | 'time';
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  hint?: string;
  ariaLabel?: string;
}> = ({ id, label, value, onChange, error, required, showRequired = false, labelClassName, className, type = 'text', inputMode, placeholder, inputRef, isMobile, disabled, hint, ariaLabel }) => (
  <Field id={id} label={label} error={error} required={required} showRequired={showRequired} labelClassName={labelClassName} className={className} hint={hint}>
    <input
      id={id}
      ref={inputRef}
      type={type}
      inputMode={inputMode || (type === 'number' ? 'decimal' : undefined)}
      disabled={disabled}
      className={inputCls({ isMobile, disabled }, Boolean(error && (error !== REQUIRED || showRequired)))}
      value={value}
      placeholder={placeholder}
      autoComplete="off"
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      {...ariaFor(id, error, showRequired)}
    />
  </Field>
);

/** Labelled select with a "Select…" option */
export const SelectField: React.FC<BaseInputProps & {
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string;
}> = ({ id, label, value, onChange, error, required, showRequired = false, labelClassName, className, options, placeholder = 'Select…', isMobile, disabled }) => (
  <Field id={id} label={label} error={error} required={required} showRequired={showRequired} labelClassName={labelClassName} className={className}>
    <select
      id={id}
      disabled={disabled}
      className={inputCls({ isMobile, disabled }, Boolean(error && (error !== REQUIRED || showRequired)))}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...ariaFor(id, error, showRequired)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </Field>
);

/** Labelled textarea */
export const TextAreaField: React.FC<BaseInputProps & { rows?: number; placeholder?: string; inputRef?: React.Ref<HTMLTextAreaElement> }> = ({
  id, label, value, onChange, error, required, showRequired = false, labelClassName, className, rows = 3, placeholder, inputRef, isMobile, disabled,
}) => (
  <Field id={id} label={label} error={error} required={required} showRequired={showRequired} labelClassName={labelClassName} className={className}>
    <textarea
      id={id}
      ref={inputRef}
      disabled={disabled}
      rows={rows}
      placeholder={placeholder}
      className={`${inputCls({ isMobile, disabled }, Boolean(error && (error !== REQUIRED || showRequired)))} resize-none`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      {...ariaFor(id, error, showRequired)}
    />
  </Field>
);
